import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  updateWidgetProperty,
  toggleEditMode,
  updateEditorAppearance,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Frame } from "scenejs";

import { EditorContext } from "../../index";
import StoryboardContext from "components/Storyboard/context";
import { Container } from "../core/container";
import { GlassThemesSettings } from "../slideRender/components";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import {
  useChildrenWidgetSelected,
  useEmptyWidgetSelection,
  useHotspotWidgetSelector,
  useWidgetSelector
} from "components/Editor/hooks/useEditorSession";

import { Widget } from "./../../widgets/core";
import { SlidesContext } from "../slideRender";
import { Button } from "react-bootstrap";

import { MediaLibraryModal } from "components/MediaLibrary";
import { DEFAULT_MEDIA_PROPERTY } from "constants/background";
import Portal from "components/Portal";

import { Property } from "../../components/properties/";

import styles from "./slide.module.scss";

const SlideWidgetComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
  children,
}) {
  const ref = widgetRef;

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const slideRenderViewer = document.getElementsByClassName(
    "slideRender_Wrapper"
  )[0];

  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');

  const visibleWitdh = _.get(widgetProps, `properties.${breakpoint}.container.minWidth`) || 1200;

  const widgetHeight = parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) ||  675;
  const hotspotWidget = useHotspotWidgetSelector(editor, breakpoint);
  const hotspotWidgetParent = useWidgetSelector(editor, hotspotWidget && hotspotWidget?.parent);
  const hotspotVisibility = _.get(hotspotWidgetParent, `properties.${breakpoint}.visible`)
  const [height, setHeight] = useState(`${widgetHeight}px`);

  const showToolbars = useEmptyWidgetSelection(editor);

  React.useEffect(() => {
    const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};
    if (layout && layout.height != height) {
      dispatch(
        updateWidgetProperty(editor, id, breakpoint, {
          layout: {
            ...layout,
            height: height,
          },
        })
      );
    }
  }, [height, children]);

  var widgetStyle = {
    ...style,
    width: breakpoint === "desktop" ? `${visibleWitdh}px` : `${visibleWitdh}px`,
    minHeight: breakpoint === "desktop" ? `675px` : `666.67px`,
  };

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.slideSection, "cs_slide_root", {
          [styles.mobile]: breakpoint === "mobile",
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={false}
        data-storyboard-type={storyboardType}
        style={widgetStyle}
      >
        { 
          children
        }

        {( hotspotWidget && hotspotVisibility ) && (
          <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
        )}

      </div>
    </Container>
  );
};

const slideWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const SlideWidget = React.memo(SlideWidgetComponent, slideWidgetEquals);


SlideWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: false,
    resizable: false,
    rotatable: false,
    selectable: false,
    cloneable: false,
    deletable: false,
    renderDirections: [],
    onResize: (widget, layout) => {
      const frame = new Frame(layout);

      if (frame.has("transform", "translate")) {
        frame.set("transform", "translate", "0px, 0px");
      }

      const rect = getBoundingRectFromLayout(layout);
      const height = Math.max(600, rect.height);

      frame.set("height", `${height}px`);

      return frame.get();
    },
    onReparent: (widget, parent) => {
      return false;
    },
  };
};

SlideWidget.processRequest = (widget) => {
  var properties = widget.properties;

  var background = _.get(properties, "desktop.background");
  var media = _.get(background, "media");
  if (background && media) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        background: {
          ...background,
          media: media.id,
        },
      },
    };
  }

  return {
    ...widget,
    properties: properties,
  };
};

export { SlideWidget };
